import { Box, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BlockIcon from '@mui/icons-material/Block';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import IEslTemplateItem from "../../../../models/esl/eslTemplateElement";
import { EEslTemplateElementMovementMode } from "../../../../models/enums/eslTemplateElementMovementMode";

interface IQrCodeElementPropretiesProps {
  selectedElement: IEslTemplateItem;
  onUpdateElement: (element: IEslTemplateItem) => void;
  templateWidth: number;
  templateHeight: number;
}

const DEFAULT_MARGIN = 20;

const QrCodeElementPropreties = ({ selectedElement, onUpdateElement, templateWidth, templateHeight }: IQrCodeElementPropretiesProps) => {
  const { t } = useTranslation(['eslTemplate', 'common']);

  const currentMode = selectedElement.movementMode ?? EEslTemplateElementMovementMode.None;
  const currentMargin = selectedElement.movementMargin ?? 0;
  const isMovementEnabled = currentMode !== EEslTemplateElementMovementMode.None;
  const movesHorizontally = currentMode === EEslTemplateElementMovementMode.Horizontal || currentMode === EEslTemplateElementMovementMode.Random;
  const movesVertically = currentMode === EEslTemplateElementMovementMode.Vertical || currentMode === EEslTemplateElementMovementMode.Random;

  const horizontalOverflow = movesHorizontally && (
    selectedElement.position.left - currentMargin < 0
    || selectedElement.position.left + selectedElement.dimensions.width + currentMargin > templateWidth
  );
  const verticalOverflow = movesVertically && (
    selectedElement.position.top - currentMargin < 0
    || selectedElement.position.top + selectedElement.dimensions.height + currentMargin > templateHeight
  );
  const hasOverflow = horizontalOverflow || verticalOverflow;

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, nextMode: EEslTemplateElementMovementMode | null) => {
    if (nextMode === null) return;

    const nextMargin = nextMode !== EEslTemplateElementMovementMode.None && currentMargin === 0
      ? DEFAULT_MARGIN
      : currentMargin;

    onUpdateElement({ ...selectedElement, movementMode: nextMode, movementMargin: nextMargin });
  };

  const handleMarginChange = (rawValue: string) => {
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed) || parsed < 0) return;

    onUpdateElement({ ...selectedElement, movementMargin: parsed });
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('eslTemplate:templateEditor.elementPropreties.serviceRequestQRCodeDesc')}
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 3 }}>
        {t('eslTemplate:templateEditor.elementPropreties.movement.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('eslTemplate:templateEditor.elementPropreties.movement.description')}
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={currentMode}
        onChange={handleModeChange}
        size="small"
        fullWidth
      >
        <Tooltip title={t('eslTemplate:templateEditor.elementPropreties.movement.modeNone')}>
          <ToggleButton value={EEslTemplateElementMovementMode.None}>
            <BlockIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('eslTemplate:templateEditor.elementPropreties.movement.modeHorizontal')}>
          <ToggleButton value={EEslTemplateElementMovementMode.Horizontal}>
            <SwapHorizIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('eslTemplate:templateEditor.elementPropreties.movement.modeVertical')}>
          <ToggleButton value={EEslTemplateElementMovementMode.Vertical}>
            <SwapVertIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('eslTemplate:templateEditor.elementPropreties.movement.modeRandom')}>
          <ToggleButton value={EEslTemplateElementMovementMode.Random}>
            <ShuffleIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      <TextField
        label={t('eslTemplate:templateEditor.elementPropreties.movement.margin')}
        type="number"
        value={currentMargin}
        onChange={(e) => handleMarginChange(e.target.value)}
        disabled={!isMovementEnabled}
        error={isMovementEnabled && hasOverflow}
        helperText={
          !isMovementEnabled
            ? t('eslTemplate:templateEditor.elementPropreties.movement.helperDisabled')
            : hasOverflow
              ? t('eslTemplate:templateEditor.elementPropreties.movement.errorOverflow')
              : t('eslTemplate:templateEditor.elementPropreties.movement.helperEnabled', { margin: currentMargin })
        }
        inputProps={{ min: 0 }}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default QrCodeElementPropreties;
